<template>
  <div>
    <!-- QR Scanner Section -->
    <q-dialog v-model="scannerOpen" maximized>
      <div class="bg-white">
        <div
          class="row justify-center"
          :style="`height: ${$store.state.pageHeight - 308}px`"
        >
          <video
            :style="$q.screen.lt.sm ? 'width: 300px;' : 'width: 500px;'"
            id="readerVideo"
          ></video>
        </div>
        <div class="row justify-center">
          <q-btn
            label="Cancel QR"
            color="secondary"
            :style="$q.screen.lt.sm ? 'width: 300px;' : 'width: 500px;'"
            @click="cancelQR"
          />
        </div>
      </div>
    </q-dialog>

    <!-- Board Info Header -->
    <div>
      <!-- For larger screens: buttons on left and right, board name in the middle -->
      <div class="row justify-between items-center q-pa-md">
        <div>
          <q-btn
            icon="arrow_back"
            color="primary"
            label="Boards"
            @click="backToBoards"
          />
        </div>
        <div v-if="$q.screen.gt.sm" class="text-h6 text-center">
          {{ TableName }}
        </div>
        <div>
          <q-btn
            color="primary"
            icon="qr_code_scanner"
            round
            size="sm"
            class="q-mr-sm"
            @click="scanQR"
          >
            <q-tooltip>Scan QR Code</q-tooltip>
          </q-btn>
          <q-btn
            color="accent"
            icon="add"
            round
            size="sm"
            class="q-mr-sm"
            @click="addLog"
          >
            <q-tooltip>Add New Log</q-tooltip>
          </q-btn>
          <q-btn
            color="grey-3"
            text-color="primary"
            icon="settings"
            round
            size="sm"
            @click="showSettingsDialog = true"
          >
            <q-tooltip>Settings</q-tooltip>
          </q-btn>
        </div>
      </div>
      <q-separator v-if="$q.screen.lt.sm" class="full-width" />

      <!-- For smaller screens: buttons on top row, board name below -->
      <div v-if="$q.screen.lt.sm">
        <div class="text-h6 text-center q-py-sm">{{ TableName }}</div>
      </div>
    </div>

    <!-- Action Bar -->
    <div class="action-bar q-mb-sm">
      <div class="row justify-between items-center q-col-gutter-sm">
        <div class="row items-center q-gutter-xs">
          <q-btn
            v-if="selectedZebraOnline"
            color="primary"
            icon="print"
            flat
            :round="$q.screen.gt.xs"
            :dense="$q.screen.gt.xs"
            size="sm"
            @click="printSelectedZebra()"
            :disable="!selectedZebraOnline"
          >
            <q-tooltip>Print to Zebra</q-tooltip>
          </q-btn>
        </div>
      </div>
    </div>

    <!-- Logs Table -->
    <div>
      <q-table
        square
        :data="BoardLogs"
        :columns="columns"
        :pagination="pagination"
        row-key="id"
        separator="cell"
        card-class="board-logs-card"
        :loading="$store.state.loading"
        no-data-label="No Board Logs"
        :dense="$q.screen.lt.md"
        :table-header-class="$q.screen.lt.sm ? 'q-table--dense' : ''"
      >
        <template v-slot:top>
          <div class="text-subtitle1 q-py-sm q-px-sm">Board Logs</div>
        </template>

        <template v-if="$q.screen.lt.sm" v-slot:body="props">
          <q-tr :props="props">
            <q-td key="Date">
              {{ parseTimeStamp(props.row.date, 'short') }}
            </q-td>
            <q-td
              key="Notes"
              class="ellipsis cursor-pointer"
              style="
                max-width: 80px;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
              "
              @click="showFullNote(props.row)"
            >
              {{ decoder(props.row.notes) }}
            </q-td>
            <q-td key="In-service" class="ellipsis" style="max-width: 80px">
              {{
                inServiceName(
                  props.row.farm_id,
                  props.row.pond_id,
                  props.row.computer_id
                )
              }}
            </q-td>
            <q-td key="Actions">
              <div class="row no-wrap justify-center">
                <q-btn
                  flat
                  round
                  size="sm"
                  color="primary"
                  icon="edit"
                  class="q-mx-sm"
                  @click="editLog(props.row)"
                />
                <q-btn
                  flat
                  round
                  size="sm"
                  :color="props.row.attachments.length ? 'primary' : 'grey-6'"
                  icon="photo_library"
                  class="q-mr-sm"
                  @click="showAttachmentClick(props.row)"
                />
                <q-btn
                  flat
                  round
                  size="sm"
                  color="negative"
                  icon="delete"
                  @click="deleteLog(props.row)"
                />
              </div>
            </q-td>
          </q-tr>
        </template>

        <template v-if="$q.screen.gt.xs" v-slot:body-cell-Date="props">
          <q-td :props="props">
            <div class="text-weight-medium">
              {{ parseTimeStamp(props.row.date, 'short') }}
            </div>
          </q-td>
        </template>

        <template v-if="$q.screen.gt.xs" v-slot:body-cell-Notes="props">
          <q-td
            :props="props"
            class="cursor-pointer"
            @click="showFullNote(props.row)"
          >
            <div
              style="
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
              "
            >
              {{ decoder(props.row.notes) }}
            </div>
          </q-td>
        </template>

        <template v-if="$q.screen.gt.xs" v-slot:body-cell-In-service="props">
          <q-td :props="props">
            <div>
              {{
                inServiceName(
                  props.row.farm_id,
                  props.row.pond_id,
                  props.row.computer_id
                )
              }}
            </div>
          </q-td>
        </template>

        <template v-if="$q.screen.gt.xs" v-slot:body-cell-Actions="props">
          <q-td :props="props" class="actions-cell">
            <div class="row no-wrap justify-center">
              <q-btn
                flat
                round
                dense
                color="primary"
                icon="edit"
                @click="editLog(props.row)"
              >
                <q-tooltip>Edit Log</q-tooltip>
              </q-btn>
              <q-btn
                flat
                round
                dense
                :color="props.row.attachments.length ? 'primary' : 'grey-6'"
                icon="photo_library"
                @click="showAttachmentClick(props.row)"
              >
                <q-tooltip>View Attachments</q-tooltip>
              </q-btn>
              <q-btn
                flat
                round
                dense
                color="negative"
                icon="delete"
                @click="deleteLog(props.row)"
              >
                <q-tooltip>Delete Log</q-tooltip>
              </q-btn>
            </div>
          </q-td>
        </template>
      </q-table>
    </div>

    <!-- Edit Log Modal -->
    <q-dialog
      v-model="showEditModal"
      persistent
      position="top"
      :maximized="$q.screen.lt.sm"
    >
      <q-card
        :style="$q.screen.lt.sm ? '' : 'width: 510px'"
        :class="$q.screen.xs ? 'q-pa-md' : 'q-pa-lg'"
      >
        <div
          class="row justify-center full-width text-h6 text-white bg-primary q-py-sm q-mb-md"
        >
          <span>{{ ModalHeader }}</span>
        </div>

        <q-form>
          <q-input
            filled
            square
            autogrow
            v-model="editLogCopy.notes"
            type="textarea"
            label="Repair Notes"
            maxlength="1024"
            class="q-mb-md"
          />

          <q-select
            v-model="editLogCopy.farm_id"
            filled
            square
            :options="FarmOptions"
            emit-value
            map-options
            label="Farm"
            class="q-mb-md"
          />

          <q-select
            v-model="locationSelection"
            filled
            square
            :options="LocationOptions"
            emit-value
            map-options
            label="Pond / Computer"
            :disable="!editLogCopy.farm_id"
            class="q-mb-md"
          />

          <q-input filled square label="Date" v-model="logDate" class="q-mb-md">
            <template v-slot:append>
              <q-icon name="event" class="cursor-pointer">
                <q-popup-proxy
                  ref="qDateProxy"
                  transition-show="scale"
                  transition-hide="scale"
                >
                  <q-date v-model="logDate" mask="MM/DD/YYYY">
                    <div class="row items-center justify-end">
                      <q-btn v-close-popup label="Close" color="primary" flat />
                    </div>
                  </q-date>
                </q-popup-proxy>
              </q-icon>
            </template>
          </q-input>

          <div class="row full-width">
            <q-space />
            <q-btn
              label="Cancel"
              color="grey-3"
              text-color="black"
              class="q-mr-sm"
              @click="cancelEdit"
            />
            <q-btn label="Save" color="primary" @click="onSubmit" />
          </div>
        </q-form>
      </q-card>
    </q-dialog>

    <!-- Attachments Dialog -->
    <q-dialog
      v-model="showAttachment"
      persistent
      :maximized="true"
      transition-show="slide-up"
      transition-hide="slide-down"
    >
      <q-card v-if="selectedLog">
        <q-card-actions>
          <q-btn
            label="Back"
            color="primary"
            icon="arrow_back"
            padding="xs sm"
            @click="showAttachment = false"
          />
        </q-card-actions>
        <q-separator />

        <q-card-section
          style="height: calc(100vh - 60px)"
          class="scroll q-pt-lg"
        >
          <Attachments :parentObj="selectedLog" :parentType="'repair-log'" />
        </q-card-section>
      </q-card>
    </q-dialog>

    <!-- Zebra Preview -->
    <ZebraPreview
      :show="showZebraPreview"
      :printData="selectedPreview"
      @complete="closeZebraPreview"
    />

    <!-- Full Note Dialog -->
    <q-dialog v-model="showNoteDialog">
      <q-card style="min-width: 350px">
        <q-card-section
          class="row items-center q-pb-none bg-primary text-white"
        >
          <div class="text-h6">Notes</div>
          <q-space />
          <q-btn icon="close" flat round dense v-close-popup />
        </q-card-section>

        <q-card-section class="q-pt-md">
          <div class="text-body1">{{ decoder(selectedNoteText) }}</div>
        </q-card-section>

        <q-card-section class="text-caption text-grey">
          {{ selectedNoteDate ? parseTimeStamp(selectedNoteDate, 'long') : '' }}
        </q-card-section>
      </q-card>
    </q-dialog>

    <!-- Settings Dialog -->
    <q-dialog v-model="showSettingsDialog">
      <q-card style="min-width: 350px">
        <q-card-section class="row items-center bg-primary text-white">
          <div class="text-h6">Board Log Settings</div>
          <q-space />
          <q-btn icon="close" flat round dense v-close-popup />
        </q-card-section>

        <q-card-section>
          <q-toggle
            v-model="autoOpenLogOnScan"
            label="Auto-open new log after scan"
            @input="saveSettings"
          />
          <div class="text-caption text-grey q-mt-sm">
            When enabled, the new log dialog opens automatically after scanning
            a QR code.
          </div>
        </q-card-section>
      </q-card>
    </q-dialog>

    <!-- Delete Confirmation Dialog -->
    <q-dialog v-model="showDeleteDialog" persistent>
      <q-card style="min-width: 350px">
        <q-card-section class="row items-center bg-primary text-white">
          <div class="text-h6">Confirm Delete</div>
          <q-space />
          <q-btn icon="close" flat round dense v-close-popup />
        </q-card-section>

        <q-card-section class="q-pt-md">
          <p class="text-body1">Are you sure you want to delete this log?</p>
        </q-card-section>

        <q-card-actions align="right">
          <q-btn
            label="Cancel"
            color="grey-3"
            text-color="black"
            v-close-popup
          />
          <q-btn label="Delete" color="negative" @click="confirmDelete" />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<script>
import Attachments from '@/components/Attachments';
import ZebraPreview from '@/components/ZebraPreview';
import { parseTimeStamp } from '@/lib/date-utils';
import { decoder, smartCompare } from '@/lib/helpers';
import store from '@/store';
import { date } from 'quasar';
import { BoardTypes } from '@/lib/board-types';

export default {
  name: 'BoardLogs',
  components: {
    Attachments,
    ZebraPreview
  },
  props: {
    boardId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      editLogCopy: {},
      selectedLog: null,
      logDate: null,
      showEditModal: false,
      scannerOpen: false,
      html5QrCode: null,
      showZebraPreview: false,
      selectedPreview: {},
      showAttachment: false,
      showNoteDialog: false,
      selectedNoteText: '',
      selectedNoteDate: null,
      showDeleteDialog: false,
      logToDelete: null,
      showSettingsDialog: false,
      autoOpenLogOnScan: true,
      pagination: {
        sortBy: 'Date',
        descending: true,
        page: 1,
        rowsPerPage: 15
      },
      columns: [
        {
          name: 'Date',
          required: true,
          label: 'Date',
          align: 'left',
          field: (row) => row.date,
          sortable: true,
          sort: (a, b) => b - a
        },
        {
          name: 'Notes',
          required: true,
          label: 'Repair Notes',
          align: 'left',
          field: 'notes',
          sortable: true,
          style: 'max-width: 250px; min-width: 150px; overflow: hidden;'
        },
        {
          name: 'In-service',
          label: 'Location',
          align: 'left',
          field: (row) => `${row.farm_id}:${row.pond_id}:${row.computer_id}`,
          sortable: true
        },
        {
          name: 'Actions',
          label: 'Actions',
          align: 'center'
        }
      ],
      boardTypes: BoardTypes
    };
  },
  mounted() {
    this.loadSettings();

    if (this.$store.state.openNewLogForBoard === parseInt(this.boardId)) {
      this.$nextTick(() => {
        setTimeout(() => {
          if (this.autoOpenLogOnScan) {
            this.addLog();
          }
          // Clear the flag after using it
          this.$store.dispatch('setOpenNewLogForBoard', null);
        }, 500);
      });
    }
  },
  computed: {
    BoardLogs() {
      const logs = this.$store.state.boardLogs.filter(
        (log) => log.board_id === parseInt(this.boardId)
      );
      return [...logs].sort((a, b) => {
        return a.date === b.date ? b.id - a.id : b.date - a.date;
      });
    },

    TableTitle() {
      return `Board Logs - ${this.boardId}`;
    },

    TableName() {
      const board = this.$store.state.boards.find(
        (brd) => brd.id === parseInt(this.boardId)
      );
      return board
        ? `${board.type} #${board.id} (${board.rev})`
        : 'Loading board details...';
    },

    ModalHeader() {
      return this.editLogCopy.id === 'new'
        ? 'New Repair Log'
        : 'Edit Repair Log';
    },

    FarmOptions() {
      const ret = [];
      ret.push({
        label: '--- Select Farm ---',
        value: null,
        name: '---'
      });

      let farmList;
      if (this.$store.getters.isSuperAdmin) {
        farmList = this.$store.state.farmList;
      } else {
        farmList = this.$store.state.farmList.filter((e) => {
          return this.$store.state.user.farms.find((f) => f === e.id);
        });
      }

      for (const farm of farmList) {
        ret.push({
          label: decoder(farm.farm_name),
          value: farm.id,
          name: farm.farm_name
        });
      }

      return ret;
    },

    LocationOptions() {
      const ret = [];
      ret.push({
        label: '--- Select Pond / Computer ---',
        value: null
      });

      const farm = this.$store.state.farmList.find(
        (farm) => farm.id === this.editLogCopy.farm_id
      );

      if (farm && farm.farmASM) {
        const ponds = [...farm.farmASM].sort((a, b) =>
          smartCompare(decoder(a.name), decoder(b.name))
        );
        for (const pond of ponds) {
          ret.push({
            label: decoder(pond.name),
            value: `pond:${pond.id}`
          });
        }
      }

      if (farm && farm.computers && farm.computers.length > 0) {
        ret.push({
          label: '--- Computers ---',
          value: null,
          disable: true
        });
        for (const computer of farm.computers) {
          ret.push({
            label: decoder(computer.name),
            value: `computer:${computer.id}`
          });
        }
      }

      return ret;
    },

    locationSelection: {
      get() {
        if (this.editLogCopy.computer_id) {
          return `computer:${this.editLogCopy.computer_id}`;
        }
        if (this.editLogCopy.pond_id) {
          return `pond:${this.editLogCopy.pond_id}`;
        }
        return null;
      },
      set(value) {
        if (!value) {
          this.editLogCopy.pond_id = null;
          this.editLogCopy.computer_id = null;
          return;
        }
        const [kind, id] = value.split(':');
        if (kind === 'pond') {
          this.editLogCopy.pond_id = parseInt(id);
          this.editLogCopy.computer_id = null;
        } else if (kind === 'computer') {
          this.editLogCopy.pond_id = null;
          this.editLogCopy.computer_id = parseInt(id);
        }
      }
    },

    selectedZebraOnline() {
      if (this.$store.state.zebraSelection === '') {
        return false;
      }
      return this.$store.state.zebraPrinters.find(
        (p) => p.zebraId === this.$store.state.zebraSelection
      );
    },

    lastLogData() {
      const logs = this.BoardLogs;
      if (logs.length === 0) {
        return '';
      }

      return decoder(logs[0].notes);
    }
  },
  methods: {
    backToBoards() {
      this.$store.dispatch('setHighlightBoardId', parseInt(this.boardId));
      this.$router.push('/boards');
    },

    async cancelQR() {
      this.scannerOpen = false;
      if (this.html5QrCode !== null) {
        await this.html5QrCode.stop();
        this.html5QrCode.destroy();
        this.html5QrCode = null;
      }
    },

    async scanQR() {
      if (this.scannerOpen) {
        return;
      }

      try {
        const QrScanner = await import('qr-scanner');
        this.scannerOpen = true;

        await this.$nextTick();

        this.html5QrCode = new QrScanner.default(
          document.getElementById('readerVideo'),
          (result) => {
            const decodedText = result.data;
            this.scannerOpen = false;
            this.html5QrCode.stop();
            this.html5QrCode.destroy();

            let foundValue = null;
            if (isNaN(parseInt(decodedText))) {
              const tail = decodedText.split('/boards/');
              foundValue = tail[1];
            } else {
              foundValue = decodedText;
            }

            if (!foundValue || isNaN(parseInt(foundValue))) {
              this.$q.notify({
                message: 'Invalid QR Code',
                color: 'negative',
                icon: 'error'
              });
            } else {
              const board_id = parseInt(foundValue);
              if (this.$store.state.boards.find((brd) => brd.id === board_id)) {
                if (board_id === parseInt(this.boardId)) {
                  // Same board - add a new log if setting enabled
                  if (this.autoOpenLogOnScan) {
                    this.addLog();
                  }
                } else {
                  // Different board - navigate to it
                  this.$store.dispatch('setOpenNewLogForBoard', board_id);
                  this.$router.push(`/boards/${foundValue}`);
                }
              } else {
                this.$q.notify({
                  message: 'Board not found',
                  color: 'negative',
                  icon: 'error'
                });
              }
              this.html5QrCode = null;
            }
          },
          {
            returnDetailedScanResult: true,
            highlightScanRegion: true,
            highlightCodeOutline: true,
            calculateScanRegion: function (video) {
              const smallestDimension = Math.min(
                video.videoWidth,
                video.videoHeight
              );
              const scanRegionSize = Math.round((1 / 2) * smallestDimension);

              return {
                x: Math.round((video.videoWidth - scanRegionSize) / 2),
                y: Math.round((video.videoHeight - scanRegionSize) / 2),
                width: scanRegionSize,
                height: scanRegionSize,
                downScaledWidth: 300,
                downScaledHeight: 300
              };
            }
          }
        );

        await this.html5QrCode.start();
      } catch (error) {
        this.$q.notify({
          message: error.toString(),
          color: 'negative',
          icon: 'error'
        });

        this.scannerOpen = false;
        if (this.html5QrCode) {
          this.html5QrCode.stop();
          this.html5QrCode.destroy();
          this.html5QrCode = null;
        }
      }
    },

    decoder(text) {
      return decoder(text);
    },

    parseTimeStamp(timestamp, format) {
      return parseTimeStamp(timestamp, format);
    },

    inServiceName(farm_id, pond_id, computer_id) {
      if (farm_id) {
        const farm = this.$store.state.farmList.find(
          (farm) => farm.id === farm_id
        );

        if (farm) {
          if (computer_id && farm.computers) {
            const computer = farm.computers.find((c) => c.id === computer_id);
            if (computer) {
              return `${decoder(farm.farm_name)} : ${decoder(computer.name)}`;
            }
          }
          const pond = farm.farmASM.find((pond) => pond.id === pond_id);
          if (pond) {
            return `${decoder(farm.farm_name)} : ${decoder(pond.name)}`;
          } else {
            return `${decoder(farm.farm_name)}`;
          }
        }
      }

      return '---';
    },

    editLog(log) {
      this.editLogCopy = JSON.parse(JSON.stringify(log));
      this.logDate = date.formatDate(
        this.editLogCopy.date * 1000,
        'MM/DD/YYYY'
      );
      this.showEditModal = true;
    },

    deleteLog(log) {
      this.logToDelete = log;
      this.showDeleteDialog = true;
    },

    confirmDelete() {
      this.showDeleteDialog = false;
      this.dispatchDelete(this.logToDelete);
    },

    dispatchDelete(log) {
      store
        .dispatch('deleteBoardLog', {
          log
        })
        .then((results) => {
          this.$finishResult.handleResultsAsync(results).then((response) => {
            if (response === 'retry') {
              this.dispatchDelete(log);
            } else {
              this.$q.notify({
                message: 'Log Deleted',
                color: 'accent',
                icon: 'check_circle'
              });
            }
          });
        });
    },

    addLog() {
      this.editLogCopy = {
        id: 'new',
        board_id: parseInt(this.boardId),
        notes: '',
        farm_id: null,
        pond_id: null,
        computer_id: null,
        attachments: [],
        date: Math.floor(Date.now() / 1000)
      };
      this.logDate = date.formatDate(Date.now(), 'MM/DD/YYYY');
      this.showEditModal = true;
    },

    onSubmit() {
      const dispatch =
        this.editLogCopy.id === 'new' ? 'createBoardLog' : 'updateBoardLog';

      this.editLogCopy.date = date.formatDate(new Date(this.logDate), 'X');

      store
        .dispatch(dispatch, {
          log: this.editLogCopy
        })
        .then((results) => {
          results.disableCheck = true;
          this.$finishResult.handleResultsAsync(results).then((response) => {
            if (response === 'retry') {
              this.onSubmit();
            } else {
              this.showEditModal = false;
              this.$q.notify({
                message: `Log ${
                  this.editLogCopy.id === 'new' ? 'created' : 'updated'
                } successfully`,
                color: 'primary',
                icon: 'check_circle'
              });
            }
          });
        });
    },

    cancelEdit() {
      this.showEditModal = false;
    },

    showAttachmentClick(row) {
      this.selectedLog = row;
      this.showAttachment = true;
    },

    printSelectedZebra() {
      const board = this.$store.state.boards.find(
        (brd) => brd.id === parseInt(this.boardId)
      );

      if (!board) return;

      this.selectedPreview = {
        type: board.type,
        id: board.id,
        rev: board.rev,
        note: board.note,
        logData: this.lastLogData
      };

      this.showZebraPreview = true;
    },

    closeZebraPreview() {
      this.showZebraPreview = false;
    },

    showFullNote(log) {
      this.selectedNoteText = log.notes;
      this.selectedNoteDate = log.date;
      this.showNoteDialog = true;
    },

    loadSettings() {
      const saved = localStorage.getItem('boardLogs-autoOpenLogOnScan');
      if (saved !== null) {
        this.autoOpenLogOnScan = saved === 'true';
      }
    },

    saveSettings() {
      localStorage.setItem(
        'boardLogs-autoOpenLogOnScan',
        this.autoOpenLogOnScan.toString()
      );
    }
  },
  watch: {
    'editLogCopy.farm_id'(newVal, oldVal) {
      if (oldVal !== undefined && newVal !== oldVal) {
        this.editLogCopy.pond_id = null;
        this.editLogCopy.computer_id = null;
      }
    },
    '$store.state.boardLogs'() {
      if (this.showAttachment === true && this.selectedLog) {
        const log = this.$store.state.boardLogs.find(
          (log) => log.id === this.selectedLog.id
        );
        if (log) {
          this.selectedLog = log;
        }
      }
    },
    '$store.state.openNewLogForBoard'(newValue) {
      if (newValue === parseInt(this.boardId)) {
        this.$nextTick(() => {
          setTimeout(() => {
            if (this.autoOpenLogOnScan) {
              this.addLog();
            }
            // Clear the flag after using it
            this.$store.dispatch('setOpenNewLogForBoard', null);
          }, 500);
        });
      }
    }
  }
};
</script>

<style></style>
